import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import StationAction from 'station/actions/StationAction'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ThresholdIndicators from './ThresholdIndicators'
import { DISABLED_LINK } from 'productionUnit/constants/ProductionUnitConstants'
import PiezometryAction from 'piezometry/actions/PiezometryAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'

const BoreholeThresholdIndicators = () => {
    const {
        associatedSites,
        piezometryDataTypes,
        hydrometryDataTypes,
    } = useSelector(store => ({
        associatedSites: store.StationReducer.associatedSites,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
    }), shallowEqual)
    const dispatch = useDispatch()

    useEffect(() => {
        if (!piezometryDataTypes.length) {
            dispatch(PiezometryAction.fetchPiezometryDataTypes())
        }
        if (!hydrometryDataTypes.length) {
            dispatch(HydrometryAction.fetchHydrometryDataTypes())
        }
    }, [])

    const [indicators, setIndicators] = useState([])

    const { isLoaded, progress } = useProgressDispatch(() => {
        const associatedPiezos = associatedSites.filter(as => as.stationLinkedType === STATION_TYPE_CONSTANT.piezometer).map(({ stationLinkedId }) => stationLinkedId)
        const associatedHydros = associatedSites.filter(as => as.stationLinkedType === STATION_TYPE_CONSTANT.hydrometry).map(({ stationLinkedId }) => stationLinkedId)
        return [
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedPiezos, STATION_TYPE_NAME.piezometer))
                .then(newIndicators => setIndicators(prev => [
                    ...prev,
                    ...newIndicators.flatMap(i => i.data).map(d => ({ ...d, type: STATION_TYPE_CONSTANT.piezometer })),
                ])),
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedHydros, STATION_TYPE_NAME.hydrologicalStation))
                .then(newIndicators => setIndicators(prev => [
                    ...prev,
                    ...newIndicators.flatMap(i => i.data).map(d => ({ ...d, type: STATION_TYPE_CONSTANT.hydrometry })),
                ])),
        ]
    }, [associatedSites])

    const indicatorsWithOrder = useMemo(() => {
        return indicators.map(ind => {
            const dataTypes = ind.type === STATION_TYPE_CONSTANT.piezometer ? piezometryDataTypes : hydrometryDataTypes
            const dataType = dataTypes.find(dt => dt.id === ind.dataType)
            return {
                ...ind,
                order: dataType?.order,
            }
        })
    }, [indicators, piezometryDataTypes, hydrometryDataTypes])

    return <ThresholdIndicators indicators={indicatorsWithOrder} isLoaded={isLoaded} progress={progress} />
}


const ProductionThresholdIndicators = () => {
    const {
        prodUnitLinks,
        piezometryDataTypes,
        hydrometryDataTypes,
    } = useSelector(store => ({
        prodUnitLinks: store.ProductionUnitReducer.prodUnitLinks,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        hydrometryDataTypes: store.HydrometryReducer.hydrometryDataTypes,
    }), shallowEqual)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!piezometryDataTypes.length) {
            dispatch(PiezometryAction.fetchPiezometryDataTypes())
        }
        if (!hydrometryDataTypes.length) {
            dispatch(HydrometryAction.fetchHydrometryDataTypes())
        }
    }, [])

    const [indicators, setIndicators] = useState([])

    const { isLoaded, progress } = useProgressDispatch(() => {
        const associatedPiezos = prodUnitLinks.filter(as => as.siteType === STATION_TYPE_CONSTANT.piezometer && as.status !== DISABLED_LINK).map(({ siteCode }) => siteCode)
        const associatedHydros = prodUnitLinks.filter(as => as.siteType === STATION_TYPE_CONSTANT.hydrometry && as.status !== DISABLED_LINK).map(({ siteCode }) => siteCode)
        return [
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedPiezos, STATION_TYPE_NAME.piezometer))
                .then(newIndicators => setIndicators(prev => [
                    ...prev,
                    ...newIndicators.flatMap(i => i.data).map(d => ({ ...d, type: STATION_TYPE_CONSTANT.piezometer })),
                ])),
            dispatch(StationAction.fetchSpecificObservatoryFollowResult(associatedHydros, STATION_TYPE_NAME.hydrologicalStation))
                .then(newIndicators => setIndicators(prev => [
                    ...prev,
                    ...newIndicators.flatMap(i => i.data).map(d => ({ ...d, type: STATION_TYPE_CONSTANT.hydrometry })),
                ])),
        ]
    }, [prodUnitLinks])

    const indicatorsWithOrder = useMemo(() => {
        return indicators.map(ind => {
            const dataTypes = ind.type === STATION_TYPE_CONSTANT.piezometer ? piezometryDataTypes : hydrometryDataTypes
            const dataType = dataTypes.find(dt => dt.id === ind.dataType)
            return {
                ...ind,
                order: dataType?.order,
            }
        })
    }, [indicators, piezometryDataTypes, hydrometryDataTypes])

    return <ThresholdIndicators indicators={indicatorsWithOrder} isLoaded={isLoaded} progress={progress}/>
}

export { BoreholeThresholdIndicators, ProductionThresholdIndicators }